import { useState } from 'react';
import { PlanOrigin, type AdminTenantDetailDto } from '../types/tenants.types';
import { AssignPlanModal } from './AssignPlanModal';
import { RevertPlanAssignmentModal } from './RevertPlanAssignmentModal';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';
import { formatIsoDate } from '@/shared/utils/format-date.utils';

interface TenantPlanSectionProps {
  tenant: AdminTenantDetailDto;
}

const ORIGIN_LABEL: Record<string, string> = {
  [PlanOrigin.SelfService]: 'Contratado por la cuenta',
  [PlanOrigin.AdminAssigned]: 'Asignado por un administrador',
};

/**
 * Origen del plan y sus acciones. Vive dentro de la tarjeta "Plan y suscripción" que ya
 * existía en el detalle del tenant, no en una tarjeta aparte que repetiría el mismo dato.
 */
export function TenantPlanSection({ tenant }: TenantPlanSectionProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);

  const isAdminAssigned = tenant.planOrigin === PlanOrigin.AdminAssigned;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 p-4 rounded-xl border border-outline">
        <span className="text-primary text-xs opacity-60 uppercase tracking-wider">
          Origen del plan
        </span>
        <span data-testid="tenant-plan-origin" className="text-light text-sm font-medium">
          {ORIGIN_LABEL[tenant.planOrigin] ?? tenant.planOrigin}
        </span>
        {isAdminAssigned && (
          <span className="text-primary text-xs">
            Por <span data-testid="tenant-assigned-by">{tenant.assignedByAdminId ?? '—'}</span> el{' '}
            <span data-testid="tenant-assigned-at">{formatIsoDate(tenant.assignedAt, 'long')}</span>
          </span>
        )}
      </div>

      <Protected permissions={PERMISSIONS.PLANS_ASSIGN_SPECIAL}>
        <div className="flex flex-wrap gap-3">
          <UnifiedButton
            variant="outline"
            onClick={() => setAssignOpen(true)}
            data-testid="assign-plan-btn"
          >
            {isAdminAssigned ? 'Cambiar plan asignado' : 'Asignar plan'}
          </UnifiedButton>

          {/* Solo tiene sentido revertir lo que un administrador otorgó. Lo que la cuenta
              contrató se cancela por los caminos normales, y la API lo rechaza. */}
          {isAdminAssigned && (
            <UnifiedButton
              variant="outline"
              onClick={() => setRevertOpen(true)}
              data-testid="revert-assignment-btn"
            >
              Revertir asignación
            </UnifiedButton>
          )}
        </div>
      </Protected>

      <AssignPlanModal
        tenantId={tenant.id}
        tenantName={tenant.name}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
      />

      <RevertPlanAssignmentModal
        tenantId={tenant.id}
        tenantName={tenant.name}
        open={revertOpen}
        onClose={() => setRevertOpen(false)}
      />
    </div>
  );
}
